import { useMemo } from 'react';
import {
  DecoratedKitRequestLine,
  useGetDecoratedKitRequestLines,
} from 'hooks/useGetDecoratedKitRequestLines';
import { DISABLE_PAGE_LIMIT, QueryFilterOperators } from 'types/api';
import { useGetKitRequestsQuery } from 'store/slices/clientV2/kitRequests';
import { KitRequestSchema } from 'types/kitting';
import { WarehouseKitRequest } from 'views/warehouse/types';
import { skipToken } from '@reduxjs/toolkit/dist/query';
import useServerSideQuery from 'hooks/useServerSideQuery';
import useLocalStorage from 'hooks/useLocalStorage';
import {
  warehouseStorageName,
  warehouseDefaults,
} from 'types/warehouseSettings';
import {
  EstimatedShipDateResult,
  findLatestExpectedStockLot,
  calculateEstimatedProcessingDate,
  calculateFinalShipDate,
  getEstimatedShipDateToRender,
  getHasEstimatedShipDateChanged,
} from 'hooks/useEstimateKitShipDate';
import {
  CalculateKitRequestStatusReturn,
  estimateHandlingRequestData,
  estimateHandlingRequestRemaining,
} from 'hooks/useGetDecoratedKitRequestLines/utils';
import { formatToUniversalDate } from 'utils/dates';
import { useFeature } from '@growthbook/growthbook-react';
import { FeatureFlags } from 'types';

/**
 * Interface defining the parameters for the useGetWarehouseKitRequestData hook
 */
export interface WarehouseKitRequestDataParams {
  /**
   * Flag to control whether to fetch kit request lines
   * @default true
   */
  fetchKitRequestLines?: boolean;
  /**
   * Page number for pagination (1-indexed)
   * Only used when fetchKitRequestLines is true
   */
  pageNumber?: number;
  /**
   * Page size for pagination
   * @default 25 when fetchKitRequestLines is true
   */
  pageSize?: number;
  /**
   * Search term to filter kit requests
   * Only used when fetchKitRequestLines is true
   */
  search?: string;
  /**
   * Flag to control whether to filter for started kit requests only
   * Only applies when temp-start-kit-workflow feature flag is enabled
   * @default true
   */
  includeStartedFilter?: boolean;
}

/**
 * Interface defining the return type of the useGetWarehouseKitRequestData hook
 */
export interface WarehouseKitRequestData<FetchLines extends boolean = true> {
  /** List of warehouse kit requests */
  kitRequests: WarehouseKitRequest[];
  /** Kit request statuses for each kit request */
  kitRequestStatuses:
    | Record<KitRequestSchema['id'], CalculateKitRequestStatusReturn>
    | undefined;
  /** Decorated kit request lines (empty array when fetchKitRequestLines is false) */
  decoratedKitRequestLines: DecoratedKitRequestLine[];
  /** Estimated ship dates for each kit request (empty object when fetchKitRequestLines is false) */
  kitRequestShipDates: FetchLines extends true
    ? Record<string, EstimatedShipDateResult>
    : Record<string, never>;
  /** Handling request data for each kit request (empty object when fetchKitRequestLines is false) */
  kitRequestHandlingData: FetchLines extends true
    ? Record<string, ReturnType<typeof estimateHandlingRequestRemaining>>
    : Record<string, never>;
  /** Total count of kit requests matching filters (only present when pagination is used) */
  totalCount?: number;
  /** Total pages available (only present when pagination is used) */
  totalPages?: number;
  /** Page size used for pagination (only present when pagination is used) */
  pageSize?: number;
  /** Whether kit requests are currently loading */
  isLoadingKitRequests: boolean;
  /** Whether kit request lines are currently loading (false when fetchKitRequestLines is false) */
  isLoadingKitRequestLines: FetchLines extends true ? boolean : false;
  /** Whether allocations are currently loading (false when fetchKitRequestLines is false) */
  isLoadingAllocations: FetchLines extends true ? boolean : false;
  /** Whether stock lots are currently loading (false when fetchKitRequestLines is false) */
  isLoadingStockLots: FetchLines extends true ? boolean : false;
  /** Function to refetch kit requests */
  refetchKitRequests: () => void;
  /** Function to refetch decorated kit request lines */
  refetchDecoratedKitRequestLines: () => void;
}

/**
 * Hook to fetch and return warehouse kit request data
 * @param params - Parameters to control the behavior of the hook
 * @returns Warehouse kit request data including kit requests, and optionally decorated lines and ship dates
 */
export const useGetWarehouseKitRequestData = <
  FetchLines extends boolean = true
>(
  params?: WarehouseKitRequestDataParams & {
    fetchKitRequestLines?: FetchLines;
    overrideKitRequestIds?: KitRequestSchema['id'][];
  }
): WarehouseKitRequestData<FetchLines> => {
  const {
    fetchKitRequestLines = true as FetchLines,
    overrideKitRequestIds,
    pageNumber = 1,
    pageSize = 25,
    search,
    includeStartedFilter = true,
  } = params || {};

  const [settings] = useLocalStorage(warehouseStorageName, warehouseDefaults);
  const tempStartKitWorkflow = useFeature(FeatureFlags.tempStartKitWorkflow).on;

  const usePagination = fetchKitRequestLines;
  const effectivePageSize = usePagination ? pageSize : DISABLE_PAGE_LIMIT;

  const baseFilters = [
    {
      field: 'sourceLocations' as const,
      operator: QueryFilterOperators.isAnyOf,
      value: [settings.facility],
    },
    {
      field: 'approvedAt' as const,
      operator: QueryFilterOperators.isNotEmpty,
      value: null,
    },
    {
      field: 'fulfilledAt' as const,
      operator: QueryFilterOperators.isEmpty,
      value: null,
    },
    {
      field: 'archived' as const,
      operator: QueryFilterOperators['='],
      value: false,
    },
  ];

  const kitRequestFilters =
    tempStartKitWorkflow && includeStartedFilter
      ? [
          ...baseFilters,
          {
            field: 'startedAt' as const,
            operator: QueryFilterOperators.isNotEmpty,
            value: null,
          },
        ]
      : baseFilters;

  const {
    data: kitRequestsResponse,
    isFetching: isLoadingKitRequests,
    refetch: refetchKitRequests,
  } = useServerSideQuery<KitRequestSchema, WarehouseKitRequest>(
    useGetKitRequestsQuery,
    settings.facility
      ? {
          pageSize: effectivePageSize,
          ...(usePagination && { pageNumber }),
          ...(search && { search, searchSchema: ['kitCode', 'name'] }),
          schema: [
            'id',
            'kitCode',
            'name',
            'externalNotes',
            'approved',
            'approvedAt',
            'archived',
            'startedAt',
            'fulfilledAt',
            'sourceLocations',
            'shipPartial',
            'carrier',
            'service',
            'targetShipDate',
            'estimatedShipDate',
            'productionRun',
            { org: ['id', 'name'] },
            {
              shipTo: [
                'id',
                'name',
                'externalName',
                'requiresReels',
                'requiresMerges',
                'requiresSplits',
                'warehouseShippingEnabled',
                'consumesStock',
                'acceptsSmtNoPnp',
              ],
            },
            { team: ['id', 'name'] },
          ],
          filters: kitRequestFilters as never,
        }
      : skipToken
  );

  const kitRequests = useMemo(
    () => kitRequestsResponse?.data ?? [],
    [kitRequestsResponse]
  );

  const {
    decoratedKitRequestLines,
    isLoadingKitRequestLines,
    isLoadingAllocations,
    isLoadingStockLots,
    refetchDecoratedKitRequestLines,
    kitRequestStatuses,
  } = useGetDecoratedKitRequestLines(
    fetchKitRequestLines
      ? {
          selectedKitRequestIds:
            overrideKitRequestIds !== undefined
              ? overrideKitRequestIds
              : kitRequests.map((kitRequest) => kitRequest.id),
          facility: settings.facility,
        }
      : { selectedKitRequestIds: [], facility: settings.facility }
  );

  // Calculate handling request data and estimated ship dates for each kit request
  const { kitRequestShipDates, kitRequestHandlingData } = useMemo(() => {
    if (
      !fetchKitRequestLines ||
      !decoratedKitRequestLines ||
      !kitRequests.length
    ) {
      return {
        kitRequestShipDates: {},
        kitRequestHandlingData: {},
      };
    }

    const shipDatesResult: Record<string, EstimatedShipDateResult> = {};
    const handlingDataResult: Record<
      string,
      ReturnType<typeof estimateHandlingRequestRemaining>
    > = {};

    // Group decorated kit request lines by kit request ID
    const linesByKitRequest = decoratedKitRequestLines.reduce((acc, line) => {
      const kitRequestId = line.kitRequest.id;
      if (!acc[kitRequestId]) {
        acc[kitRequestId] = [];
      }
      acc[kitRequestId].push(line);
      return acc;
    }, {} as Record<string, DecoratedKitRequestLine[]>);

    // Calculate ship date and handling data for each kit request
    kitRequests.forEach((kitRequest) => {
      const kitRequestLines = linesByKitRequest[kitRequest.id] || [];

      // Skip if no lines found for this kit request
      if (kitRequestLines.length === 0) {
        shipDatesResult[kitRequest.id] = {
          estimatedShipDate: null,
          estimatedProcessingDate: null,
          latestExpectedStockLot: null,
          estimatedShipDateToRender: null,
          hasEstimatedShipDateChanged: false,
        };
        handlingDataResult[kitRequest.id] = estimateHandlingRequestRemaining({
          decoratedKitRequestLines: [],
          shipToLocation: kitRequest.shipTo,
        });
        return;
      }

      // Calculate handling request data for this kit request (needed for ship date calculations)
      const handlingRequestData = estimateHandlingRequestData({
        decoratedKitRequestLines: kitRequestLines,
        shipToLocation: kitRequest.shipTo,
      });

      // Calculate remaining handling request data (to be returned in results)
      const handlingRequestRemaining = estimateHandlingRequestRemaining({
        decoratedKitRequestLines: kitRequestLines,
        shipToLocation: kitRequest.shipTo,
      });

      handlingDataResult[kitRequest.id] = handlingRequestRemaining;

      // Calculate ship date using the imported utility functions
      const latestExpectedStockLot =
        findLatestExpectedStockLot(kitRequestLines);
      const estimatedProcessingDate = calculateEstimatedProcessingDate(
        kitRequestLines,
        handlingRequestData
      );
      const estimatedShipDate = calculateFinalShipDate(
        kitRequestLines,
        estimatedProcessingDate,
        latestExpectedStockLot,
        kitRequest.shipPartial
      );

      shipDatesResult[kitRequest.id] = {
        estimatedShipDate,
        estimatedProcessingDate: estimatedProcessingDate
          ? formatToUniversalDate(estimatedProcessingDate)
          : null,
        latestExpectedStockLot,
        estimatedShipDateToRender: getEstimatedShipDateToRender({
          kitRequest,
          estimatedShipDate,
        }),
        hasEstimatedShipDateChanged: getHasEstimatedShipDateChanged({
          kitRequest,
          estimatedShipDate,
        }),
      };
    });

    return {
      kitRequestShipDates: shipDatesResult,
      kitRequestHandlingData: handlingDataResult,
    };
  }, [fetchKitRequestLines, decoratedKitRequestLines, kitRequests]);

  const totalCount = usePagination ? kitRequestsResponse?.count : undefined;
  const totalPages = usePagination ? kitRequestsResponse?.pages : undefined;

  return {
    kitRequests,
    kitRequestStatuses,
    decoratedKitRequestLines: fetchKitRequestLines
      ? decoratedKitRequestLines
      : [],
    kitRequestShipDates: kitRequestShipDates as FetchLines extends true
      ? Record<string, EstimatedShipDateResult>
      : Record<string, never>,
    kitRequestHandlingData: kitRequestHandlingData as FetchLines extends true
      ? Record<string, ReturnType<typeof estimateHandlingRequestRemaining>>
      : Record<string, never>,
    totalCount,
    totalPages,
    pageSize: usePagination ? effectivePageSize : undefined,
    isLoadingKitRequests,
    isLoadingKitRequestLines: fetchKitRequestLines
      ? isLoadingKitRequestLines
      : false,
    isLoadingAllocations: fetchKitRequestLines ? isLoadingAllocations : false,
    isLoadingStockLots: fetchKitRequestLines ? isLoadingStockLots : false,
    refetchKitRequests,
    refetchDecoratedKitRequestLines,
  } as WarehouseKitRequestData<FetchLines>;
};
